import { useCallback } from "react"
import { IconTableOutline18 as FileSpreadsheet } from "nucleo-ui-outline-18"
import { z } from "zod"

import { Button, Form, FormInput, Label, useZodForm } from "@productlane/ui"

import { toast } from "@/components/toast"

// Windows reports .csv files as an Excel type, so the name decides too.
export const isCsvFile = (file: File | undefined) =>
	!!file &&
	(file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv"))

interface UploadStepProps {
	onSuccess: (file: File) => void
}

export function UploadStep({ onSuccess }: UploadStepProps) {
	const form = useZodForm({
		schema: z.object({
			file: z.instanceof(FileList),
		}),
	})

	const file = form.watch("file")

	const handleSubmit = useCallback(
		({ file }: { file: FileList }) => {
			const csv = file.item(0)
			if (csv) {
				onSuccess(csv)
			} else {
				toast.error({ title: "There was an Error uploading your file" })
			}
		},
		[onSuccess],
	)

	return (
		<Form form={form} onSubmit={handleSubmit} className="space-y-4">
			<Label htmlFor="file-upload">
				<div
					onDrop={(e) => {
						e.preventDefault()
						if (isCsvFile(e.dataTransfer.files[0])) {
							form.setValue("file", e.dataTransfer.files)
						} else {
							toast.error({ title: "Only CSV files are allowed" })
						}
					}}
					onDragOver={(e) => e.preventDefault()}
					className="mt-2 flex flex-col items-center rounded-lg border border-dashed border-gray-400/60 bg-white px-6 py-10 dark:border-gray-500 dark:bg-gray-900"
				>
					<FileSpreadsheet />
					<div className="mt-4">
						{file && file.length > 0 ? (
							<div>{file[0]?.name}</div>
						) : (
							<>
								<span className="font-medium text-primary">Upload a file</span>
								<span className="pl-1 font-normal text-secondary">
									or drag and drop
								</span>
							</>
						)}
					</div>
				</div>
				<FormInput
					id="file-upload"
					type="file"
					name="file"
					accept=".csv"
					className="sr-only"
				/>
			</Label>

			<Button
				className="float-right"
				type="submit"
				// size="lg"
				disabled={!file || file.length === 0}
			>
				Upload
			</Button>
		</Form>
	)
}
